import type { Dispatch, SetStateAction } from "react"

import type { UseMutationResult } from "react-query"
import { useSelector } from "react-redux"
import type { Edge, Node } from "reactflow"
import { Position, getConnectedEdges, getIncomers, getOutgoers } from "reactflow"

import { NotificationUtils } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import dagre from "dagrejs"
import type { FormikProps } from "formik"
import { v4 as uuidv4 } from "uuid"

import type { RootState } from "../../../store/ReduxStore"
import type { CreateWorkflowRequest, UpdateWorkflowRequest } from "../../../types/custom/projects"
import type { FilterCondition } from "../../../types/custom/rules"
import type { ModelDeploymentSchemaFeature } from "../../../types/generated/api/ModelDeploymentSchemaFeature"
import type { ScriptSchemaFeature } from "../../../types/generated/api/ScriptSchemaFeature"
import { WorkflowConstantFeature } from "../../../types/generated/api/WorkflowConstantFeature"
import { WorkflowCreateRequest } from "../../../types/generated/api/WorkflowCreateRequest"
import type { WorkflowDeploymentConstantFeature } from "../../../types/generated/api/WorkflowDeploymentConstantFeature"
import type { WorkflowDeploymentSchemaFeature } from "../../../types/generated/api/WorkflowDeploymentSchemaFeature"
import { WorkflowInputFeature } from "../../../types/generated/api/WorkflowInputFeature"
import type { WorkflowList } from "../../../types/generated/api/WorkflowList"
import type { WorkflowVersionRetrieve } from "../../../types/generated/api/WorkflowVersionRetrieve"
import { convertSingleRuleToCondition } from "../../../utils/conditionHelpers"
import { convertComplexConditionsToNestedForm } from "../../../utils/rulesetHelpers"
import { EMPTY_FILTER_CONDITION, edgeStyles, markerEnd } from "../fixtures/workflow-fixtures"
import {
  type BaseNodeData,
  type ComputedFeatures,
  type ConstantFeature,
  type ExtendedNodeData,
  type FeatureData,
  type FeatureMapping,
  MapNodeTypeToFlowFormatEnum,
  type SchemaFeature,
  type UpdateGraphParams,
  type WorkflowNode,
} from "../types/interfaces"

/**
 * Validates that feature mapped values are in correct format to be sent to backend.
 * Each value must have node_features and workflow_feature keys.
 * If feature is mapped to itself, removes feature from array.
 *
 * @param feature_mappings - Array of feature mappings to validate
 * @returns Validated array of feature mappings
 */
export function validateFeatureMapping(feature_mappings: FeatureMapping[]): FeatureMapping[] {
  let mappings: (FeatureMapping | null)[] = []

  if (feature_mappings?.length > 0) {
    mappings = feature_mappings?.map((feature: FeatureMapping | null) => {
      if ([null, undefined, ""].includes(feature?.workflow_feature?.toString()?.trim())) {
        return null
      }

      return feature
    })
  }

  return mappings.filter((mapping) => mapping !== null)
}

// this function returns an array of nodes based on the node type selected, that should be added
// to the workflow, this is because some nodes are terminating (no children) and some have pre-defined
// children for them
// ruleset-node -> 1 children (uncovered route)
// filter-node -> 2 children (true route, false route)
// live model-node -> terminator
// end result-node -> terminator
export const preDefinedRoutesForNodes = (
  nodeData: {
    nodeType: string
    newNodeId: string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    updateGraph: any
    parentId: string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    schema?: any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formik?: any
    filterBranch: string
    isNodeInFilter: boolean
    filterId: string
    isNodeInLoop: boolean
    isNodeInNestedLoop: boolean
    loopId: string
    scriptType?: "marketplace" | "custom"
  },
  // eslint-disable-next-line
  selectionNodeList: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any => {
  const {
    filterBranch,
    filterId,
    isNodeInFilter,
    newNodeId,
    nodeType,
    parentId,
    updateGraph,
    formik,
    schema,
    isNodeInLoop,
    isNodeInNestedLoop,
    loopId: parentLoopId,
    scriptType,
  } = nodeData

  if (nodeType === "RulesetNode" || nodeType === "DecisionNode") {
    const id = uuidv4()
    const newRulsetNode = {
      id: newNodeId,
      position: {
        x: 0,
        y: 0,
      },
      type: "RulesetNode",
      data: {
        updateGraph,
        ruleset: "",
        uncovered_route: null,
        filterBranch,
        filterId,
        isNodeInFilter,
        node_type: "ruleset",
        nodeType,
        rulesets: selectionNodeList,
        parentId,
        editMode: false,
        readMode: false,
        createMode: true,
        schema,
        formik,
      },
    }
    const addBlockNode = {
      id: `${id}`,
      height: 60,
      width: 125,
      position: {
        x: 0,
        y: 0,
      },
      type: "AddBlockNode",
      data: {
        updateGraph,
        parentId: newNodeId,
        schema,
        filterBranch,
        filterId,
        isNodeInFilter,
        formik,
      },
    }
    return [newRulsetNode, addBlockNode]
  } else if (nodeType === "ProgramNode") {
    const id = uuidv4()

    const newProgramNode = {
      id: newNodeId,
      position: {
        x: 0,
        y: 0,
      },
      type: "ProgramNode",
      data: {
        updateGraph,
        program: "",
        node_type: "program",
        nodeType,
        programs: selectionNodeList,
        filterBranch,
        filterId,
        isNodeInFilter,
        resolution_route: null,
        parentId,
        editMode: false,
        readMode: false,
        createMode: true,
        schema,
        formik,
      },
    }

    const addBlockNode = {
      id: `${id}`,
      height: 60,
      width: 125,
      position: {
        x: 0,
        y: 0,
      },
      type: "AddBlockNode",
      data: {
        updateGraph,
        parentId: newNodeId,
        filterBranch,
        filterId,
        isNodeInFilter,
        schema,
        formik,
      },
    }
    return [newProgramNode, addBlockNode]
  } else if (nodeType === "TaglistNode") {
    const id = uuidv4()

    const newTaglistNode = {
      id: newNodeId,
      position: {
        x: 0,
        y: 0,
      },
      type: "TaglistNode",
      data: {
        updateGraph,
        taglist: "",
        node_type: "taglist",
        nodeType,
        taglists: selectionNodeList,
        resolution_route: null,
        parentId,
        editMode: false,
        readMode: false,
        createMode: true,
        filterBranch,
        filterId,
        isNodeInFilter,
        schema,
        formik,
      },
    }

    const addBlockNode = {
      id: `${id}`,
      height: 60,
      width: 125,
      position: {
        x: 0,
        y: 0,
      },
      type: "AddBlockNode",
      data: {
        updateGraph,
        parentId: newNodeId,
        filterBranch,
        filterId,
        isNodeInFilter,
        schema,
        formik,
      },
    }
    return [newTaglistNode, addBlockNode]
  } else if (nodeType === "ScriptNode") {
    const id = uuidv4()

    const newScriptNode = {
      id: newNodeId,
      position: {
        x: 0,
        y: 0,
      },
      type: "ScriptNode",
      data: {
        formik,
        feature_mappings: [],
        updateGraph,
        script: "",
        node_type: "script",
        scriptType: scriptType ?? "custom",
        computed_feature_name: "",
        nodeType,
        scripts: selectionNodeList,
        resolution_route: null,
        file: "",
        is_draft: true,
        parentId,
        editMode: false,
        readMode: false,
        createMode: true,
      },
    }

    const addBlockNode = {
      id: `${id}`,
      height: 60,
      width: 125,
      position: {
        x: 0,
        y: 0,
      },
      type: "AddBlockNode",
      data: {
        updateGraph,
        parentId: newNodeId,
        filterBranch,
        filterId,
        isNodeInFilter,
      },
    }
    return [newScriptNode, addBlockNode]
  } else if (nodeType === "ProjectNode") {
    const id = uuidv4()

    const newProjectNode = {
      id: newNodeId,
      position: {
        x: 0,
        y: 0,
      },
      type: "ProjectNode",
      data: {
        updateGraph,
        project: "",
        computed_feature_name: "",
        node_type: "project",
        nodeType,
        projects: selectionNodeList,
        resolution_route: null,
        filterBranch,
        filterId,
        isNodeInFilter,
        parentId,
        editMode: false,
        readMode: false,
        createMode: true,
        schema,
        formik,
      },
    }

    const addBlockNode = {
      id: `${id}`,
      height: 60,
      width: 125,
      position: {
        x: 0,
        y: 0,
      },
      type: "AddBlockNode",
      data: {
        updateGraph,
        parentId: newNodeId,
        filterBranch,
        filterId,
        isNodeInFilter,
        schema,
        formik,
      },
    }
    return [newProjectNode, addBlockNode]
  } else if (nodeType === "EndResultNode") {
    const newEndResultNode = {
      id: newNodeId,
      position: {
        x: 0,
        y: 0,
      },
      type: "LabeledNode",
      data: {
        node_type: "label",
        return_label: {
          name: "",
          uuid: "",
        },
        description: "",
        nodeType,
        updateGraph,
        filterBranch,
        filterId,
        isNodeInFilter,
        parentId,
        formik,
      },
    }
    return [newEndResultNode]
  } else if (nodeType === "FilterNode") {
    const id = uuidv4()
    const newFilterNode = {
      id: newNodeId,
      position: {
        x: 0,
        y: 0,
      },
      type: "FilterNode",
      data: {
        node_type: "filter",
        nodeType,
        feature_nesting_operator: ".",
        rules: [EMPTY_FILTER_CONDITION],
        condition_list_files: [],
        filterBranch,
        filterId: newNodeId,
        isNodeInFilter,
        true_route: null,
        false_route: null,
        editMode: false,
        readMode: false,
        updateGraph,
        parentId,
        parentLoopId,
        isNodeInLoop,
        isNodeInNestedLoop,
        schema,
        formik,
      },
    }
    const addBlockNodeTrue = {
      id: `${id}`,
      position: {
        x: 0,
        y: 0,
      },
      height: 60,
      width: 125,
      type: "AddBlockNode",
      data: {
        updateGraph,
        parentId: newNodeId,
        is_true_route: true,
        nodeType: "AddBlockNode",
        filterBranch: "true",
        filterId: newNodeId,
        isNodeInFilter: true,
        schema,
        formik,
      },
    }
    const addBlockNodeFalse = {
      id: `${uuidv4()}`,
      position: {
        x: 0,
        y: 0,
      },
      type: "AddBlockNode",
      height: 60,
      width: 125,
      data: {
        updateGraph,
        parentId: newNodeId,
        filterBranch: "false",
        filterId: newNodeId,
        isNodeInFilter: true,
        is_false_route: true,
        schema,
        nodeType: "AddBlockNode",
        formik,
      },
    }
    return [newFilterNode, addBlockNodeTrue, addBlockNodeFalse]
  } else if (nodeType === "CalculatorNode") {
    const id = uuidv4()
    const newCalculatorNode = {
      id: newNodeId,
      position: {
        x: 0,
        y: 0,
      },
      type: "CalculatorNode",
      data: {
        node_type: "calculator",
        computed_feature_name: "",
        calculators: selectionNodeList,
        calculator: "",
        updateGraph,
        filterBranch,
        filterId,
        isNodeInFilter,
        nodeType,
        parentId,
        schema,
        formik,
      },
    }

    const addBlockNode = {
      id: `${id}`,
      height: 60,
      width: 125,
      position: {
        x: 0,
        y: 0,
      },
      type: "AddBlockNode",
      data: {
        updateGraph,
        uncovered_route: null,
        parentId: newNodeId,
        filterBranch,
        filterId,
        isNodeInFilter,
        schema,
        formik,
      },
    }
    return [newCalculatorNode, addBlockNode]
  } else if (nodeType === "LoopStartNode") {
    const id = uuidv4()
    const newLoopStartNode = {
      id: newNodeId,
      position: {
        x: 0,
        y: 0,
      },
      type: "LoopStartNode",
      data: {
        node_type: "loop-start",
        feature_nesting_operator: ".",
        filterBranch,
        filterId,
        isNodeInFilter,
        input_lists: null,
        loopId: newNodeId,
        isNodeInLoop,
        isNodeInNestedLoop,
        parentLoopId,
        loopName: "",
        strategy: "Parallel",
        updateGraph,
        nodeType,
        parentId,
        schema,
        formik,
      },
    }

    const newLoopEndNode = {
      id: `${id}`,
      position: {
        x: 0,
        y: 0,
      },
      type: "LoopEndNode",
      data: {
        resolution_route: null,
        node_type: "loop-end",
        parentId: newNodeId,
        parentLoopId: newNodeId,
        filterBranch,
        filterId,
        isNodeInFilter,
        nodeType: "LoopEndNode",
        parentLoopName: "",
      },
    }

    const addBlockNode = {
      id: `${uuidv4()}`,
      height: 60,
      width: 125,
      position: {
        x: 0,
        y: 0,
      },
      type: "AddBlockNode",
      data: {
        updateGraph,
        uncovered_route: null,
        parentId: id,
        filterBranch,
        filterId,
        isNodeInFilter,
        schema,
        formik,
      },
    }
    return [newLoopStartNode, newLoopEndNode, addBlockNode]
  } else if (nodeType === "ScorecardsetNode") {
    const id = uuidv4()
    const newScorecardsetNode = {
      id: newNodeId,
      position: {
        x: 0,
        y: 0,
      },
      type: "ScorecardsetNode",
      data: {
        updateGraph,
        scorecard_set: "",
        resolution_route: null,
        node_type: "scorecardset",
        nodeType,
        scorecardsets: selectionNodeList,
        parentId,
        editMode: false,
        readMode: false,
        createMode: true,
        filterBranch,
        filterId,
        isNodeInFilter,
        schema,
        formik,
      },
    }
    const addBlockNode = {
      id: `${id}`,
      height: 60,
      width: 125,
      position: {
        x: 0,
        y: 0,
      },
      type: "AddBlockNode",
      data: {
        updateGraph,
        filterBranch,
        filterId,
        isNodeInFilter,
        parentId: newNodeId,
        schema,
        formik,
      },
    }
    return [newScorecardsetNode, addBlockNode]
  }
}

// this function acts as a converter from the node object structure that we store before creating
// a request and the final format we need to send to the backend to create a workflow request
// it extracts the node.data object which is holds all the necessary keys we want to send as well as
// adding the index that points to the child of the parent node
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const parseWorkflow = (nodes: Node[], edges: Edge[]): any => {
  const filteredNodes = nodes?.filter((node) => node?.data?.nodeType !== "StartNode" && node?.type !== "AddBlockNode")

  const alteredNodes = filteredNodes?.map((node) => {
    if (node?.data?.nodeType === "RulesetNode" || node?.data?.nodeType === "DecisionNode") {
      const targetNode = edges.find((edge) => edge.source === node?.id)?.target
      const uncoveredNodeIndex = filteredNodes.findIndex((innerNode) => innerNode.id === targetNode)

      node.data = {
        ...node.data,
        ruleset: typeof node?.data?.ruleset === "string" ? node?.data?.ruleset : node?.data?.ruleset?.uuid,
        uncovered_route: uncoveredNodeIndex,
      }
    } else if (node?.data?.nodeType === "CalculatorNode") {
      const targetNode = edges.find((edge) => edge.source === node?.id)?.target
      const resolutionNodeIndex = filteredNodes.findIndex((innerNode) => innerNode.id === targetNode)

      node.data = {
        ...node.data,
        calculator: typeof node?.data?.calculator === "string" ? node?.data?.calculator : node?.data?.calculator?.uuid,
        computed_feature_name: node?.data?.computed_feature_name,
        equation: node?.data?.equation,
        resolution_route: resolutionNodeIndex,
      }
    } else if (node?.data?.nodeType === "ProgramNode") {
      const targetNode = edges.find((edge) => edge.source === node?.id)?.target
      const resolutionNodeIndex = filteredNodes.findIndex((innerNode) => innerNode.id === targetNode)

      node.data = {
        ...node.data,
        program: typeof node?.data?.program === "string" ? node?.data?.program : node?.data?.program?.uuid,
        resolution_route: resolutionNodeIndex,
      }
    } else if (node?.data?.nodeType === "TaglistNode") {
      const targetNode = edges.find((edge) => edge.source === node?.id)?.target
      const resolutionNodeIndex = filteredNodes.findIndex((innerNode) => innerNode.id === targetNode)

      node.data = {
        ...node.data,
        taglist: typeof node?.data?.taglist === "string" ? node?.data?.taglist : node?.data?.taglist?.uuid,
        resolution_route: resolutionNodeIndex,
      }
    } else if (node?.data?.nodeType === "LoopStartNode") {
      const firstNodeInLoopEdge = edges?.find((edge) => edge?.source === node?.id)

      const firstNodeInLoopIndex = filteredNodes?.findIndex((node) => node?.id === firstNodeInLoopEdge?.target)

      const loopEndNodeIndex = filteredNodes.findIndex(
        (filteredNode) =>
          filteredNode?.data?.parentLoopId === node?.data?.loopId && filteredNode?.data?.nodeType === "LoopEndNode",
      )

      node.data = {
        ...node?.data,
        node_type: "loop-start",
        name: node?.data?.loopName,
        input_lists: node?.data?.input_lists,
        feature_nesting_separator: ".",
        strategy: node?.data?.strategy?.toUpperCase(),
        start_node: firstNodeInLoopIndex,
        loop_end_node: loopEndNodeIndex,
      }
    } else if (node?.data?.nodeType === "LoopEndNode") {
      const targetNode = edges.find((edge) => edge.source === node?.id)?.target
      const resolutionNodeIndex = filteredNodes.findIndex((innerNode) => innerNode.id === targetNode)

      node.data = {
        ...node?.data,
        node_type: "loop-end",
        resolution_route: resolutionNodeIndex,
      }
    } else if (node?.data?.nodeType === "ScriptNode") {
      const { feature_mappings, ...rest } = node.data
      const targetNode = edges.find((edge) => edge.source === node?.id)?.target
      const resolutionNodeIndex = filteredNodes.findIndex((innerNode) => innerNode.id === targetNode)

      if (rest?.isBuiltin || !!rest?.script?.template) {
        node.data = {
          ...rest,
          script: typeof node?.data?.script === "string" ? node?.data?.script : node?.data?.script?.uuid,
          computed_feature_name: node?.data?.computed_feature_name,
          is_draft: false,
          resolution_route: resolutionNodeIndex,
          /**
           * first if feature mappings array is empty (no input features found),
           * then we send an empty array
           * else, if it's not empty .. we check on each value (mapped workflow feature)
           * if any of the values is empty we assign it to the same value as the key (script feature)
           * because by default each script feature is mapped to itself from the workflow schema
           */
          feature_mappings: validateFeatureMapping(feature_mappings),
        }
      } else {
        return {
          ...rest,
          script: typeof rest.script === "string" ? rest.script : rest.script?.uuid,
          computed_feature_name: rest.computed_feature_name,
          is_draft: rest.is_draft,
          resolution_route: resolutionNodeIndex,
        }
      }
    } else if (node?.data?.nodeType === "ProjectNode") {
      const { feature_mappings } = node.data
      const resolutionNodeIndex = filteredNodes.findIndex((innerNode) => innerNode?.data?.parentId === node?.id)

      if (resolutionNodeIndex !== -1) {
        node.data = {
          ...node.data,
          project: typeof node?.data?.project === "string" ? node?.data?.project : node?.data?.project?.uuid,
          computed_feature_name: node?.data?.computed_feature_name,
          resolution_route: resolutionNodeIndex,
          feature_mappings: validateFeatureMapping(feature_mappings),
        }
      } else {
        delete node?.data["resolution_route"]
        node.data = {
          ...node.data,
          project: typeof node?.data?.project === "string" ? node?.data?.project : node?.data?.project?.uuid,
          feature_mappings: validateFeatureMapping(feature_mappings),
        }
      }
    } else if (node?.data?.nodeType === "ScorecardsetNode") {
      const targetNode = edges.find((edge) => edge.source === node?.id)?.target
      const resolutionNodeIndex = filteredNodes.findIndex((innerNode) => innerNode.id === targetNode)

      node.data = {
        ...node.data,
        scorecard_set:
          typeof node?.data?.scorecard_set === "string" ? node?.data?.scorecard_set : node?.data?.scorecard_set?.uuid,
        resolution_route: resolutionNodeIndex,
      }
    } else if (node?.type === "FilterNode") {
      const trueFalseEdges = edges.filter((edge) => edge.source === node?.id)

      const trueNodeId = trueFalseEdges?.find((edge) => edge?.data?.text === "true")?.target

      const falseNodeId = trueFalseEdges?.find((edge) => edge?.data?.text === "false")?.target

      let trueRoute: null | number = null
      let falseRoute: null | number = null

      trueRoute = filteredNodes?.findIndex((node) => node?.id === trueNodeId)
      falseRoute = filteredNodes?.findIndex((node) => node?.id === falseNodeId)

      const newData = {
        condition: `${convertSingleRuleToCondition<FilterCondition>(node?.data?.rules)}`,
        true_route: trueRoute,
        false_route: falseRoute,
      }

      if (node?.data?.fileData?.uuid) {
        node.data = {
          ...node.data,
          ...newData,
        }
      } else {
        const temp = { ...node?.data, ...newData }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { condition_list_files, ...newTempObj } = temp
        node.data = { ...newTempObj }
      }
    }
    return node?.data
  })

  return {
    nodes: [...alteredNodes],
  }
}

/**
 * based on Kahn’s algorithm for Topological Sorting
 * for more info: https://www.geeksforgeeks.org/topological-sorting-indegree-based-solution/
 * @param nodes
 * @returns array of ordered nodes
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const topologicalSort = (nodes: any[]): ExtendedNodeData[] => {
  // Map to keep track of the in-degree (number of incoming edges) for each node
  const inDegree = new Map<string, number>()
  // Map to store the adjacency list (list of neighbors) for each node
  const adjacencyList = new Map<string, string[]>()

  // Initialize in-degree and adjacency list for each node
  nodes.forEach((node) => {
    inDegree.set(node?.uuid, 0)
    adjacencyList.set(node?.uuid, [])
  })

  // Build the in-degree map and adjacency list by examining parent-child relationships
  nodes.forEach((node) => {
    // Find the parent node of the current node by checking if it matches any of the known parent properties
    const parentNodeId = nodes.find((innerNode) =>
      [
        innerNode?.start_node?.uuid,
        innerNode?.true_route?.uuid,
        innerNode?.false_route?.uuid,
        innerNode?.resolution_route?.uuid,
        innerNode?.uncovered_route?.uuid,
      ].includes(node?.uuid),
    )?.uuid

    // If a parent node is found, update the in-degree and adjacency list
    if (parentNodeId) {
      // Increase the in-degree of the current node
      inDegree.set(node?.uuid, (inDegree.get(node?.uuid) || 0) + 1)
      // Add the current node to the adjacency list of the parent node
      adjacencyList.get(parentNodeId)?.push(node?.uuid)
    }
  })

  // Initialize a queue to process nodes with zero in-degree
  const queue: string[] = []
  // Array to store the sorted nodes
  const sortedNodes: Node[] = []

  // Add all nodes with zero in-degree to the queue
  inDegree.forEach((degree, nodeId) => {
    if (degree === 0) {
      queue.push(nodeId)
    }
  })

  // Process nodes in the queue
  while (queue.length > 0) {
    // Remove a node from the front of the queue
    const current = queue.shift()
    // Find the node object corresponding to the current node ID
    const currentNode = nodes.find((node) => node?.uuid === current)

    // If the node exists, add it to the sorted nodes array
    if (currentNode) {
      sortedNodes.push(currentNode)
    }

    // Get the neighbors of the current node from the adjacency list
    const neighbors = adjacencyList.get(current as string) || []
    neighbors.forEach((neighbor) => {
      // Decrease the in-degree of each neighbor
      inDegree.set(neighbor, (inDegree.get(neighbor) || 0) - 1)
      // If the in-degree of a neighbor becomes zero, add it to the queue
      if (inDegree.get(neighbor) === 0) {
        queue.push(neighbor)
      }
    })
  }

  // Check if the number of sorted nodes is equal to the number of input nodes
  // If not, the graph has a cycle or is not a Directed Acyclic Graph (DAG)
  if (sortedNodes.length !== nodes.length) {
    console.warn("Graph has a cycle or is not a Directed Acyclic Graph")
  }

  // Return the sorted nodes
  return sortedNodes
}

// this function return the necessary key for each node depending on its type
// so when each node gets parsed after retrieving the request, it should has all the needed props
// stored inside of it
const nodeDataBasedOnType = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  node: any,
  type: "retrieve" | "edit",
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  nodes: any[],
  convertedNode: Node,
  flowNodes: Node[],
  startNode?: string | null,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any => {
  const readMode = type === "retrieve"

  const parentId =
    nodes?.find((innerNode) =>
      [
        innerNode?.start_node?.uuid,
        innerNode?.true_route?.uuid,
        innerNode?.false_route?.uuid,
        innerNode?.resolution_route?.uuid,
        innerNode?.uncovered_route?.uuid,
      ].includes(node?.uuid),
    )?.uuid ?? "1"

  let data: BaseNodeData = {
    readMode,
    parentId,
  }

  const nodeInFilterData = isNodeInFilter(parentId, flowNodes, node?.uuid)

  const nodeInLoop = isNodeInLoop(parentId, flowNodes)

  if (node?.uuid === startNode) {
    data = { ...data, parentId: "1" }
  }

  if (node?.node_type === "ruleset") {
    data = {
      ...data,
      ...convertedNode?.data,
      isNodeInLoop: nodeInLoop.isNodeInLoop,
      isNodeInNestedLoop: nodeInLoop.isNodeInNestedLoop,
      loopId: nodeInLoop.loopId,
      parentLoopId: nodeInLoop.loopId,

      // filter states
      isNodeInFilter: nodeInFilterData?.isNodeInFilter,
      filterBranch: nodeInFilterData?.filterBranch,
      filterId: nodeInFilterData?.filterId,

      nodeType: node?.ruleset?.type === "DECISION" ? "DecisionNode" : "RulesetNode",
      ruleset: node?.ruleset,
      rulesetName: node?.ruleset?.name,
      rules: node?.ruleset?.rules,
      node_type: "ruleset",
      readMode,
      uncovered_route: node?.uncovered_route?.uuid,
    }
  }

  if (node?.node_type === "calculator") {
    data = {
      ...data,
      ...convertedNode?.data,
      isNodeInLoop: nodeInLoop.isNodeInLoop,
      isNodeInNestedLoop: nodeInLoop.isNodeInNestedLoop,
      loopId: nodeInLoop.loopId,
      parentLoopId: nodeInLoop.loopId,

      // filter states
      isNodeInFilter: nodeInFilterData?.isNodeInFilter,
      filterBranch: nodeInFilterData?.filterBranch,
      filterId: nodeInFilterData?.filterId,
      nodeType: "CalculatorNode",
      node_type: "calculator",
      computed_feature_name: node?.computed_feature_name,
      calculator: node?.calculator,
      readMode,
      resolution_route: node?.resolution_route?.uuid,
    }
  }
  if (node?.node_type === "scorecardset") {
    data = {
      ...data,
      ...convertedNode?.data,
      nodeType: "ScorecardsetNode",
      scorecard_set: node?.scorecard_set,
      isNodeInLoop: nodeInLoop.isNodeInLoop,
      isNodeInNestedLoop: nodeInLoop.isNodeInNestedLoop,
      loopId: nodeInLoop.loopId,
      parentLoopId: nodeInLoop.loopId,

      // filter states
      isNodeInFilter: nodeInFilterData?.isNodeInFilter,
      filterBranch: nodeInFilterData?.filterBranch,
      filterId: nodeInFilterData?.filterId,
      node_type: "scorecardset",
      resolution_route: node?.resolution_route?.uuid,
      readMode,
    }
  }
  if (node?.node_type === "program") {
    data = {
      ...data,
      ...convertedNode?.data,
      nodeType: "ProgramNode",
      program: node?.program,
      node_type: "program",
      readMode,
      isNodeInLoop: nodeInLoop.isNodeInLoop,
      isNodeInNestedLoop: nodeInLoop.isNodeInNestedLoop,
      loopId: nodeInLoop.loopId,
      parentLoopId: nodeInLoop.loopId,

      // filter states
      isNodeInFilter: nodeInFilterData?.isNodeInFilter,
      filterBranch: nodeInFilterData?.filterBranch,
      filterId: nodeInFilterData?.filterId,
      resolution_route: node?.resolution_route?.uuid,
    }
  }
  if (node?.node_type === "taglist") {
    data = {
      ...data,
      ...convertedNode?.data,
      nodeType: "TaglistNode",
      taglist: node?.taglist,
      node_type: "taglist",
      readMode,
      isNodeInLoop: nodeInLoop.isNodeInLoop,
      isNodeInNestedLoop: nodeInLoop.isNodeInNestedLoop,
      loopId: nodeInLoop.loopId,
      parentLoopId: nodeInLoop.loopId,

      // filter states
      isNodeInFilter: nodeInFilterData?.isNodeInFilter,
      filterBranch: nodeInFilterData?.filterBranch,
      filterId: nodeInFilterData?.filterId,
      resolution_route: node?.resolution_route?.uuid,
    }
  }
  if (node?.node_type === "script") {
    data = {
      ...data,
      ...convertedNode?.data,
      nodeType: "ScriptNode",
      feature_mappings: node?.feature_mappings,
      script: node?.script,
      node_type: "script",
      scriptName: node?.script?.name,
      file: node?.script?.file,
      readMode,
      computed_feature_name: node?.computed_feature_name,
      isNodeInLoop: nodeInLoop.isNodeInLoop,
      isNodeInNestedLoop: nodeInLoop.isNodeInNestedLoop,
      loopId: nodeInLoop.loopId,
      parentLoopId: nodeInLoop.loopId,
      scriptType: node?.script?.template ? "marketplace" : "custom",

      // filter states
      isNodeInFilter: nodeInFilterData?.isNodeInFilter,
      filterBranch: nodeInFilterData?.filterBranch,
      filterId: nodeInFilterData?.filterId,
      resolution_route: node?.resolution_route?.uuid,
    }
  }
  if (node?.node_type === "project") {
    const resolutionNode = nodes?.find((innerNode) => innerNode?.uuid === node?.resolution_route?.uuid)

    data = {
      ...data,
      ...convertedNode?.data,
      nodeType: "ProjectNode",
      feature_mappings: node?.feature_mappings,
      computed_feature_name: node?.computed_feature_name,
      project: node?.project,
      node_type: "project",
      readMode,
      hasChildren: !!resolutionNode,
      isNodeInLoop: nodeInLoop.isNodeInLoop,
      isNodeInNestedLoop: nodeInLoop.isNodeInNestedLoop,
      loopId: nodeInLoop.loopId,
      parentLoopId: nodeInLoop.loopId,

      // filter states
      isNodeInFilter: nodeInFilterData?.isNodeInFilter,
      filterBranch: nodeInFilterData?.filterBranch,
      filterId: nodeInFilterData?.filterId,
      resolution_route: node?.resolution_route?.uuid,
    }
  }
  if (node?.node_type === "decision") {
    data = {
      ...data,
      ...convertedNode?.data,
      rulesetName: node?.ruleset?.name,
      rules: node?.ruleset?.rules,
      ruleset: node?.ruleset,
      node_type: "decision",
      nodeType: "DecisionNode",
      isNodeInLoop: nodeInLoop.isNodeInLoop,
      isNodeInNestedLoop: nodeInLoop.isNodeInNestedLoop,
      loopId: nodeInLoop.loopId,
      parentLoopId: nodeInLoop.loopId,

      // filter states
      isNodeInFilter: nodeInFilterData?.isNodeInFilter,
      filterBranch: nodeInFilterData?.filterBranch,
      filterId: nodeInFilterData?.filterId,
      readMode,
      uncovered_route: node?.uncovered_route?.uuid,
    }
  }
  if (node?.node_type === "label") {
    data = {
      ...data,
      ...convertedNode?.data,
      isNodeInLoop: nodeInLoop.isNodeInLoop,
      isNodeInNestedLoop: nodeInLoop.isNodeInNestedLoop,
      loopId: nodeInLoop.loopId,
      parentLoopId: nodeInLoop.loopId,

      // filter states
      isNodeInFilter: nodeInFilterData?.isNodeInFilter,
      filterBranch: nodeInFilterData?.filterBranch,
      filterId: nodeInFilterData?.filterId,
      nodeType: "EndResultNode",
      return_label: node?.return_label,
      description: node?.description,
      node_type: "label",
      readMode,
    }
  }

  if (node?.node_type === "loop-end") {
    const loopStartNode = nodes.find((innerNode) => innerNode?.loop_end_node?.uuid === node?.uuid)

    data = {
      ...data,
      ...convertedNode?.data,
      nodeType: "LoopEndNode",
      parentLoopId: loopStartNode?.uuid,
      parentLoopName: loopStartNode?.name,
      isNodeInLoop: nodeInLoop.isNodeInLoop,
      isNodeInNestedLoop: nodeInLoop.isNodeInNestedLoop,
      readMode,

      // filter states
      isNodeInFilter: nodeInFilterData?.isNodeInFilter,
      filterBranch: nodeInFilterData?.filterBranch,
      filterId: nodeInFilterData?.filterId,
      resolution_route: node?.resolution_route?.uuid,
    }
  }

  if (node?.node_type === "loop-start") {
    const parentNode = nodes?.find((node) => node?.uuid === parentId)
    const nodeInLoop = isNodeInLoop(parentId, flowNodes)

    data = {
      ...data,
      ...convertedNode?.data,
      nodeType: "LoopStartNode",
      loopName: node?.name,
      strategy: node?.strategy,
      readMode,
      loopId: node?.uuid,
      input_lists: node?.input_lists,
      isNodeInLoop: nodeInLoop.isNodeInLoop,
      isNodeInNestedLoop: nodeInLoop.isNodeInLoop || parentNode?.node_type === "loop-start",

      // filter states
      isNodeInFilter: nodeInFilterData?.isNodeInFilter,
      filterBranch: nodeInFilterData?.filterBranch,
      filterId: nodeInFilterData?.filterId,
      start_node: node?.start_node?.uuid,
      loop_end_node: node?.loop_end_node?.uuid,
    }
  }

  if (node?.node_type === "filter") {
    const { rules } = convertComplexConditionsToNestedForm(node?.condition)

    return {
      ...data,
      ...convertedNode?.data,
      readMode: type === "retrieve",
      editMode: type !== "retrieve",

      rules: rules[0],
      fileData: node.condition_list_files,
      condition: node?.condition,
      condition_list_files: node.condition_list_files,
      node_type: "filter",
      nodeType: "FilterNode",
      isNodeInLoop: nodeInLoop.isNodeInLoop,
      isNodeInNestedLoop: nodeInLoop.isNodeInNestedLoop,
      loopId: nodeInLoop.loopId,
      parentLoopId: nodeInLoop.loopId,
      true_route: node?.true_route?.uuid,
      false_route: node?.false_route?.uuid,

      // filter states
      isNodeInFilter: nodeInFilterData?.isNodeInFilter,
      filterBranch: nodeInFilterData?.filterBranch,
      filterId: node?.uuid,
      areBranchesSwapped: false,
    }
  }

  return data
}

// this function retrieves the returned nodes from the backend and parse the whole nodes and edges array
export const convertRetrievedNodesToFlow = (
  // eslint-disable-next-line
  nodes: any,
  startNodeUUID: string | null,
  type: "retrieve" | "edit",
  updateGraph: (updatedData: UpdateGraphParams) => void,
  // eslint-disable-next-line
  schema: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formik: FormikProps<any>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): { nodes: any[]; edges: any[] } => {
  const CONDITIONAL_NODE_ID = uuidv4()

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sortedNodes: Array<any> = topologicalSort(nodes)

  const flowNodes: Array<ExtendedNodeData> = [
    {
      id: "1",
      data: { nodeType: "StartNode" },
      position: { x: 100, y: 100 },
      type: "StartNode",
      draggable: true,
      height: 64,
      width: 320,
    },
  ]

  const flowEdges = [
    {
      id: "e1-2",
      source: "1",
      target: startNodeUUID,
      animated: true,
      type: "custom",
      data: { updateGraph, readMode: type === "retrieve" },
      markerEnd,
      style: edgeStyles,
    },
  ]

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sortedNodes?.forEach((node: any) => {
    flowNodes.push({
      id: node?.uuid,
      height: 64,
      width: 320,
      node_type: node?.node_type,
      data: {
        updateGraph,
        formik,
        schema,
        nodeType:
          MapNodeTypeToFlowFormatEnum[node?.node_type as keyof typeof MapNodeTypeToFlowFormatEnum] ?? "LabeledNode",
      },
      position: { x: 0, y: 0 },
      type: MapNodeTypeToFlowFormatEnum[node?.node_type as keyof typeof MapNodeTypeToFlowFormatEnum] ?? "LabeledNode",
    })

    if (type === "edit" && node?.node_type === "project" && node?.resolution_route === null) {
      flowNodes.push({
        id: CONDITIONAL_NODE_ID,
        height: 64,
        width: 320,
        data: { updateGraph, readMode: false, parentId: node?.uuid, parentNodeType: "project" },
        position: { x: 0, y: 0 },
        type: MapNodeTypeToFlowFormatEnum["addBlock"],
      })
    }
  })
  flowNodes?.forEach((node: Node): void => {
    if (node?.id !== "1") {
      const originalNode = sortedNodes?.find((innerNode) => innerNode?.uuid === node.id)

      node.data = {
        ...node?.data,
        ...nodeDataBasedOnType(
          originalNode,
          type,
          sortedNodes,
          flowNodes?.find((node) => node.id === originalNode?.uuid) as Node,
          flowNodes,
          startNodeUUID,
        ),
      }
    }
  })

  flowNodes?.forEach((node) => {
    if (node?.node_type === "ruleset" || node?.node_type === "decision") {
      const uncoveredRouteEdge = {
        id: `${uuidv4()}`,
        source: node?.id,
        animated: false,
        target: node?.data?.uncovered_route,
        data: { text: "Uncovered", updateGraph, readMode: type === "retrieve" },
        type: "custom",
        markerEnd,
        style: edgeStyles,
        targetHandle: `${uuidv4()}`,
      }
      flowEdges.push(uncoveredRouteEdge)
    } else if (node?.node_type === "loop-start") {
      const loopEndEdge = {
        id: `${uuidv4()}`,
        source: node?.id,
        animated: false,
        target: node?.data?.start_node,
        data: { updateGraph, readMode: type === "retrieve" },
        type: "custom",
        markerEnd: markerEnd,
        style: edgeStyles,
        labelShowBg: false,
        targetHandle: `${uuidv4()}`,
      }
      flowEdges.push(loopEndEdge)
    } else if (
      ["program", "calculator", "scorecardset", "script", "taglist", "loop-end"].includes(node?.node_type as string)
    ) {
      const resolvesToRouteEdge = {
        id: `${uuidv4()}`,
        source: node?.id,
        animated: false,
        target: node?.data?.resolution_route,
        data: { updateGraph, readMode: type === "retrieve" },
        type: "custom",
        markerEnd: markerEnd,
        style: edgeStyles,
        labelShowBg: false,
        targetHandle: `${uuidv4()}`,
      }
      flowEdges.push(resolvesToRouteEdge)
    } else if (node?.node_type === "project") {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const resolutionNode = flowNodes?.find((innerNode: any) => innerNode?.id === node?.data?.resolution_route)
      if (resolutionNode || (type === "edit" && !resolutionNode)) {
        const resolvesToRouteEdge = {
          id: `${uuidv4()}`,
          source: node?.id,
          animated: false,
          sourceHandle: "a",
          target: type === "edit" && !resolutionNode ? CONDITIONAL_NODE_ID : node?.data?.resolution_route,
          type: "custom",
          data: { updateGraph, readMode: type === "retrieve" },
          markerEnd: markerEnd,
          style: edgeStyles,
          targetHandle: `${uuidv4()}`,
        }
        flowEdges.push(resolvesToRouteEdge)
      }
    } else if (node?.node_type === "label") {
      const currentNode = flowNodes?.find((innerNode) => innerNode.id === node?.id)
      const loopStartNode = flowNodes?.find(
        (innerNode) => innerNode?.id === currentNode?.data?.loopId && innerNode?.data?.nodeType === "LoopStartNode",
      )

      if (currentNode?.data?.isNodeInLoop) {
        const visualLabelEdge = {
          id: `${uuidv4()}`,
          source: node?.id,
          animated: true,
          target: loopStartNode?.data?.loop_end_node as string,
          sourceHandle: "a",
          type: "custom",
          data: { text: "true", updateGraph, readMode: type === "retrieve" },
          markerEnd,
          style: edgeStyles,
          targetHandle: `${uuidv4()}`,
        }

        flowEdges.push(visualLabelEdge)
      }
    } else if (node?.node_type === "filter") {
      const trueRouteEdge = {
        id: `${uuidv4()}`,
        source: node?.id,
        animated: false,
        target: node?.data?.true_route,
        sourceHandle: "a",
        type: "custom",
        data: { text: "true", updateGraph, readMode: type === "retrieve" },
        markerEnd,
        style: edgeStyles,
        targetHandle: `${uuidv4()}`,
      }
      const falseRouteEdge = {
        id: `${uuidv4()}`,
        source: node?.id,
        target: node?.data?.false_route,
        sourceHandle: "b",
        animated: false,
        type: "custom",
        data: { text: "false", updateGraph, readMode: type === "retrieve" },
        markerEnd,
        style: edgeStyles,
        targetHandle: `${uuidv4()}`,
      }
      flowEdges.push(trueRouteEdge, falseRouteEdge)
    }
  })

  return { nodes: flowNodes, edges: flowEdges }
}

export const nodeBuilder = (
  parentId: string,
  nodeType: string,
  updateGraph: (data: UpdateGraphParams) => void,
  isNodeInLoopState: { isNodeInLoop: boolean; isNodeInNestedLoop: boolean; loopId: string },
  isNodeInFilterState?: {
    isNodeInFilter: boolean
    filterBranch: string | null
    filterId: string
  },
  optionalProps?: Record<string, unknown>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any => {
  if (nodeType === "DecisionNode" || nodeType === "RulesetNode") {
    return [
      {
        id: uuidv4(),
        position: {
          x: 0,
          y: 0,
        },
        type: "RulesetNode",
        data: {
          // loop states
          isNodeInLoop: isNodeInLoopState.isNodeInLoop,
          isNodeInNestedLoop: isNodeInLoopState.isNodeInNestedLoop,
          loopId: isNodeInLoopState.loopId,

          // filter states
          isNodeInFilter: isNodeInFilterState?.isNodeInFilter,
          filterBranch: isNodeInFilterState?.filterBranch,
          filterId: isNodeInFilterState?.filterId,

          updateGraph,
          ruleset: "",
          uncovered_route: null,
          node_type: "ruleset",
          nodeType,
          rulesets: [],
          parentId,
        },
      },
    ]
  } else if (nodeType === "ScriptNode") {
    return [
      {
        id: uuidv4(),
        position: {
          x: 0,
          y: 0,
        },
        type: "ScriptNode",
        data: {
          updateGraph,
          // loop states
          isNodeInLoop: isNodeInLoopState.isNodeInLoop,
          isNodeInNestedLoop: isNodeInLoopState.isNodeInNestedLoop,
          loopId: isNodeInLoopState.loopId,

          // filter states
          isNodeInFilter: isNodeInFilterState?.isNodeInFilter,
          filterBranch: isNodeInFilterState?.filterBranch,
          filterId: isNodeInFilterState?.filterId,

          script: "",
          feature_mappings: [],
          node_type: "script",
          computed_feature_name: "",
          nodeType,
          scripts: [],
          resolution_route: null,
          file: "",
          is_draft: true,
          parentId,
          ...optionalProps,
        },
      },
    ]
  } else if (nodeType === "CalculatorNode") {
    return [
      {
        id: uuidv4(),
        position: {
          x: 0,
          y: 0,
        },
        type: "CalculatorNode",
        data: {
          updateGraph,
          // loop states
          isNodeInLoop: isNodeInLoopState.isNodeInLoop,
          isNodeInNestedLoop: isNodeInLoopState.isNodeInNestedLoop,
          loopId: isNodeInLoopState.loopId,

          // filter states
          isNodeInFilter: isNodeInFilterState?.isNodeInFilter,
          filterBranch: isNodeInFilterState?.filterBranch,
          filterId: isNodeInFilterState?.filterId,

          node_type: "calculator",
          computed_feature_name: "",
          calculator: "",
          calculators: [],
          feature_nesting_operator: ".",
          nodeType,
          parentId,
        },
      },
    ]
  } else if (nodeType === "LoopStartNode") {
    const id = uuidv4()
    const newLoopStartNode = {
      id,
      position: {
        x: 0,
        y: 0,
      },
      type: "LoopStartNode",
      data: {
        node_type: "loop-start",
        input_lists: null,
        isNodeInLoop: isNodeInLoopState.isNodeInLoop,
        isNodeInNestedLoop: isNodeInLoopState.isNodeInLoop,

        // filter states
        isNodeInFilter: isNodeInFilterState?.isNodeInFilter,
        filterBranch: isNodeInFilterState?.filterBranch,
        filterId: isNodeInFilterState?.filterId,
        feature_nesting_operator: ".",
        loopId: id,
        parentLoopId: isNodeInLoopState.loopId,
        loopName: "",
        strategy: "Parallel",
        updateGraph,
        nodeType,
        parentId,
      },
    }

    const newLoopEndNode = {
      id: `${uuidv4()}`,
      position: {
        x: 0,
        y: 0,
      },
      type: "LoopEndNode",
      data: {
        resolution_route: null,
        isNodeInLoop: isNodeInLoopState.isNodeInLoop,
        isNodeInNestedLoop: isNodeInLoopState.isNodeInNestedLoop,

        // filter states
        isNodeInFilter: isNodeInFilterState?.isNodeInFilter,
        filterBranch: isNodeInFilterState?.filterBranch,
        filterId: isNodeInFilterState?.filterId,
        node_type: "loop-end",
        parentId: id,
        parentLoopId: id,
        nodeType: "LoopEndNode",
        parentLoopName: "",
      },
    }

    return [newLoopStartNode, newLoopEndNode]
  } else if (nodeType === "FilterNode") {
    const id = uuidv4()
    const newFilterNode = {
      id,
      position: {
        x: 0,
        y: 0,
      },
      type: "FilterNode",
      data: {
        node_type: "filter",
        nodeType,
        feature_nesting_operator: ".",
        rules: [EMPTY_FILTER_CONDITION],
        isNodeInLoop: isNodeInLoopState.isNodeInLoop,
        isNodeInNestedLoop: isNodeInLoopState.isNodeInNestedLoop,
        loopId: isNodeInLoopState.loopId,
        parentLoopId: isNodeInLoopState.loopId,

        // filter states
        isNodeInFilter: isNodeInFilterState?.isNodeInFilter,
        filterBranch: isNodeInFilterState?.filterBranch,
        filterId: id,

        condition_list_files: [],
        true_route: null,
        false_route: null,
        editMode: false,
        readMode: false,
        updateGraph,
        parentId,
      },
    }

    if (isNodeInLoopState.isNodeInLoop) {
      return [newFilterNode]
    } else {
      const addBlockNodeFalse = {
        id: `${uuidv4()}`,
        position: {
          x: 0,
          y: 0,
        },
        type: "AddBlockNode",
        height: 60,
        width: 125,
        data: {
          updateGraph,
          parentId: id,
          filterBranch: "false",
          filterId: id,
          isNodeInFilter: true,
          is_false_route: true,
          nodeType: "AddBlockNode",
        },
      }

      return [newFilterNode, addBlockNodeFalse]
    }
  } else if (nodeType === "EndResultNode") {
    return [
      {
        id: uuidv4(),
        position: {
          x: 0,
          y: 0,
        },
        type: "LabeledNode",
        data: {
          node_type: "label",
          return_label: {
            name: "",
            uuid: "",
          },
          nodeType,
          updateGraph,
          isNodeInLoop: isNodeInLoopState.isNodeInLoop,
          isNodeInNestedLoop: isNodeInLoopState.isNodeInNestedLoop,
          loopId: isNodeInLoopState.loopId,

          // filter states
          isNodeInFilter: isNodeInFilterState?.isNodeInFilter,
          filterBranch: isNodeInFilterState?.filterBranch,
          filterId: isNodeInFilterState?.filterId,
          parentId,
        },
      },
    ]
  } else if (nodeType === "ScorecardsetNode") {
    return [
      {
        id: uuidv4(),
        position: {
          x: 0,
          y: 0,
        },
        type: "ScorecardsetNode",

        data: {
          updateGraph,
          isNodeInLoop: isNodeInLoopState.isNodeInLoop,
          isNodeInNestedLoop: isNodeInLoopState.isNodeInNestedLoop,
          loopId: isNodeInLoopState.loopId,
          scorecard_set: "",
          resolution_route: null,

          // filter states
          isNodeInFilter: isNodeInFilterState?.isNodeInFilter,
          filterBranch: isNodeInFilterState?.filterBranch,
          filterId: isNodeInFilterState?.filterId,
          node_type: "scorecardset",
          nodeType,
          parentId,
        },
      },
    ]
  } else if (nodeType === "ProgramNode") {
    return [
      {
        id: uuidv4(),
        position: {
          x: 0,
          y: 0,
        },
        type: "ProgramNode",
        data: {
          updateGraph,
          isNodeInLoop: isNodeInLoopState.isNodeInLoop,
          isNodeInNestedLoop: isNodeInLoopState.isNodeInNestedLoop,
          loopId: isNodeInLoopState.loopId,
          program: "",
          node_type: "program",
          nodeType,
          resolution_route: null,
          parentId,
          // filter states
          isNodeInFilter: isNodeInFilterState?.isNodeInFilter,
          filterBranch: isNodeInFilterState?.filterBranch,
          filterId: isNodeInFilterState?.filterId,
        },
      },
    ]
  } else if (nodeType === "TaglistNode") {
    return [
      {
        id: uuidv4(),
        position: {
          x: 0,
          y: 0,
        },
        type: "TaglistNode",
        data: {
          updateGraph,
          isNodeInLoop: isNodeInLoopState.isNodeInLoop,
          isNodeInNestedLoop: isNodeInLoopState.isNodeInNestedLoop,
          loopId: isNodeInLoopState.loopId,
          taglist: "",
          node_type: "taglist",
          nodeType,
          resolution_route: null,
          parentId,
          // filter states
          isNodeInFilter: isNodeInFilterState?.isNodeInFilter,
          filterBranch: isNodeInFilterState?.filterBranch,
          filterId: isNodeInFilterState?.filterId,
        },
      },
    ]
  } else if (nodeType === "ProjectNode") {
    return [
      {
        id: uuidv4(),
        position: {
          x: 0,
          y: 0,
        },
        type: "ProjectNode",
        data: {
          updateGraph,
          isNodeInLoop: isNodeInLoopState.isNodeInLoop,
          isNodeInNestedLoop: isNodeInLoopState.isNodeInNestedLoop,
          loopId: isNodeInLoopState.loopId,
          project: "",
          computed_feature_name: "",
          node_type: "project",
          nodeType,
          resolution_route: null,
          parentId,
          feature_mappings: [],

          // filter states
          isNodeInFilter: isNodeInFilterState?.isNodeInFilter,
          filterBranch: isNodeInFilterState?.filterBranch,
          filterId: isNodeInFilterState?.filterId,
        },
      },
    ]
  }
}

const propertyNameBasedOnNodeType = (type: string): string => {
  switch (type) {
    case "ruleset":
      return "ruleset"
    case "calculator":
      return "calculator"
    case "filter":
      return "filter"
    case "label":
      return "return_label"
    case "decision":
      return "decision"
    case "program":
      return "program"
    case "taglist":
      return "taglist"
    case "project":
      return "project"
    case "scorecardset":
      return "scorecardset"
    case "script":
      return "script"
    case "loop-start":
      return "loop-start"
    default:
      return "ruleset"
  }
}

/**
 * Checks if the values of two script nodes are equivalent. The function specifically compares the script UUID
 * and feature mappings between two nodes.
 *
 * @param updatedNode The updated node object which includes script and feature mappings details.
 * @param retrievedNode The node object retrieved from a database or API, to compare against the updated node.
 * @returns {boolean} Returns `true` if the nodes are considered equivalent, otherwise `false`.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const areScriptNodeValuesEqual = (updatedNode: any, retrievedNode: any): boolean => {
  // Compare the script UUIDs of both nodes; if they don't match, return false.
  if (updatedNode?.script !== retrievedNode?.script?.uuid) {
    return false
  }

  // Check if either node has feature mappings; proceed to detailed comparison if so.
  else if (updatedNode?.feature_mappings?.length > 0 || retrievedNode?.feature_mappings?.length > 0) {
    // Compare the lengths of the feature mappings arrays; if they don't match, return false.
    if (updatedNode?.feature_mappings?.length !== retrievedNode?.feature_mappings?.length) {
      return false
    } else {
      // Iterate over each object in the feature mappings arrays and compare specific properties.
      return updatedNode?.feature_mappings.every((item1: FeatureMapping, index: number) => {
        const item2 = retrievedNode?.feature_mappings[index]
        // Compare both 'node_feature' and 'workflow_feature' properties of the mapping objects.
        return item1?.node_feature === item2?.node_feature && item1?.workflow_feature === item2?.workflow_feature
      })
    }
  }
  // If there are no feature mappings to compare, return true as the script UUIDs are already confirmed to match.
  return true
}

/**
 * Checks if the values of two loop start nodes are equivalent. The function specifically compares
 * the strategy, name, and input lists between two nodes.
 *
 * @param updatedNode The updated node object which includes strategy, name, and input lists details.
 * @param retrievedNode The node object retrieved from a database or API, to compare against the updated node.
 * @returns {boolean} Returns `true` if the nodes are considered equivalent, otherwise `false`.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const areLoopStartNodeValuesEqual = (updatedNode: any, retrievedNode: any): boolean => {
  // Compare the strategy and name of both nodes; if they don't match, return false.
  if (updatedNode?.strategy !== retrievedNode?.strategy || updatedNode?.name !== retrievedNode?.name) {
    return false
  }

  // Check if either node has input lists; proceed to detailed comparison if so.
  else if (updatedNode?.input_lists?.length > 0 || retrievedNode?.input_lists?.length > 0) {
    // Compare the lengths of the input lists arrays; if they don't match, return false.
    if (updatedNode?.input_lists?.length !== retrievedNode?.input_lists?.length) {
      return false
    } else {
      // Iterate over each item in the input lists arrays and compare them.
      return updatedNode?.input_lists.every(
        (item1: string, index: number) => item1 === retrievedNode?.input_lists[index],
      )
    }
  }
  // If there are no input lists to compare, return true as the strategy and name are already confirmed to match.
  return true
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const areFilterNodeValuesEqual = (updatedNode: any, retrievedNode: any): boolean => {
  if (updatedNode?.areBranchesSwapped) {
    return false
  }

  if (updatedNode?.condition === retrievedNode?.condition) {
    if (updatedNode?.condition_list_files && retrievedNode?.condition_list_files) {
      return updatedNode?.condition_list_files?.uuid === retrievedNode?.condition_list_files?.uuid
    }

    return true
  } else {
    return false
  }
}

const areNodesEqual = (retrievedNodes: Node[] | undefined, currentNodes: Node[], edges: Edge[]): boolean => {
  const nodesToSubmit = parseWorkflow(currentNodes, edges)
  let equal = true

  if (currentNodes && retrievedNodes && retrievedNodes?.length === nodesToSubmit?.nodes?.length) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    retrievedNodes?.forEach((node: any) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const existingNode = nodesToSubmit?.nodes?.find((innerNode: any) => {
        if (innerNode?.node_type === node?.node_type) {
          const property = propertyNameBasedOnNodeType(innerNode?.node_type)

          if ((property === "ruleset" || property === "decision") && innerNode?.ruleset === node?.ruleset?.uuid)
            return innerNode

          if (
            property === "calculator" &&
            innerNode?.equation === node?.equation &&
            innerNode?.computed_feature_name === node?.computed_feature_name
          )
            return innerNode

          if (property === "scorecardset" && innerNode?.scorecard_set === node?.scorecard_set?.uuid) return innerNode
          if (property === "taglist" && innerNode?.taglist === node?.taglist?.uuid) return innerNode
          if (property === "program" && innerNode?.program === node?.program?.uuid) return innerNode
          if (property === "project" && innerNode?.project === node?.project?.uuid) return innerNode
          if (property === "script" && areScriptNodeValuesEqual(innerNode, node)) return innerNode
          if (property === "filter" && areFilterNodeValuesEqual(innerNode, node)) return innerNode
          if (property === "loop-start" && areLoopStartNodeValuesEqual(innerNode, node)) return innerNode
        }
      })

      if (existingNode) {
        nodesToSubmit?.nodes?.splice(nodesToSubmit?.nodes?.indexOf(existingNode), 1)
      } else {
        equal = false
        return false
      }
    })
  } else {
    equal = false
    return equal
  }
  return equal
}

// helper function to indicate workflow have any incomplete nodes or not
const isWorkflowInComplete = (nodes: Node[]): boolean => {
  const addBlockNode = nodes.find((node: Node) => node.type === "AddBlockNode")
  if (addBlockNode) {
    const parentNode = nodes.find((node: Node) => node?.id === addBlockNode?.data?.parentId)
    if (parentNode && parentNode?.data?.node_type !== "project" && addBlockNode?.data?.parentNodeType !== "project") {
      return true
    }
  }

  return false
}

/**
 * this helper function extracts only the relevant key fields in each node's data to prepare it
 * to be sent in the create/update workflow payload
 * @param  {WorkflowNode} node
 * @returns {WorkflowNode}
 */
const filterNodesKeys = (node: WorkflowNode): WorkflowNode => {
  const rulesetNodeKeys = ["node_type", "uncovered_route", "ruleset"]
  const filterNodeKeys = [
    "node_type",
    "feature_nesting_operator",
    "true_route",
    "false_route",
    "condition",
    "condition_list_files",
  ]
  const taglistNodeKeys = ["taglist", "node_type", "resolution_route"]
  const scorecardsetNodeKeys = ["scorecard_set", "node_type", "resolution_route"]
  const programNodeKeys = ["program", "node_type", "resolution_route"]
  const calculatorNodeKeys = ["node_type", "calculator", "resolution_route"]
  const scriptNodeKeys = ["node_type", "computed_feature_name", "script", "feature_mappings", "resolution_route"]
  const projectNodeKeys = ["node_type", "computed_feature_name", "project", "feature_mappings", "resolution_route"]
  const labelNodeKeys = ["node_type", "return_label", "description"]
  const loopStartNodeKeys = [
    "node_type",
    "feature_nesting_operator",
    "input_lists",
    "start_node",
    "loop_end_node",
    "name",
    "strategy",
  ]

  const loopEndNodeKeys = ["node_type", "resolution_route"]

  let validKeys: string[]

  switch (node.node_type) {
    case "ruleset":
      validKeys = rulesetNodeKeys
      break
    case "filter":
      validKeys = filterNodeKeys
      break
    case "taglist":
      validKeys = taglistNodeKeys
      break
    case "scorecardset":
      validKeys = scorecardsetNodeKeys
      break
    case "program":
      validKeys = programNodeKeys
      break
    case "calculator":
      validKeys = calculatorNodeKeys
      break
    case "script":
      validKeys = scriptNodeKeys
      break
    case "project":
      validKeys = projectNodeKeys
      break
    case "label":
      validKeys = labelNodeKeys
      break
    case "loop-start":
      validKeys = loopStartNodeKeys
      break
    case "loop-end":
      validKeys = loopEndNodeKeys
      break
    default:
      validKeys = []
  }

  const filteredNode: WorkflowNode = {} as WorkflowNode

  validKeys?.forEach((key) => {
    if (node.hasOwnProperty(key)) {
      filteredNode[key as keyof WorkflowNode] = node[key as keyof WorkflowNode] as string
    }
  })

  return filteredNode
}

const validateWorkflow = (
  nodes: Node[],
  edges: Edge[],
  workflowsData?: WorkflowList[],
  workflowName?: string,
  type?: "update" | "create",
  retrievedWorkflowName?: string,
): void | { nodes: Array<WorkflowNode>; startNode: number } => {
  const areNodesInComplete = isWorkflowInComplete(nodes)

  const duplicateNames = workflowsData?.filter((wf: WorkflowList) => {
    if (type === "update") {
      return wf?.name === workflowName && workflowName !== retrievedWorkflowName
    } else {
      return wf?.name === workflowName
    }
  })

  const correctFormat = parseWorkflow([...nodes], edges)

  let incompleteNodes = null
  const ISOpattern = /^\(\d{4}-\d{2}-\d{2}\)$/
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  correctFormat?.nodes?.forEach((node: any) => {
    if (node?.node_type === "ruleset" && !node?.ruleset) {
      incompleteNodes = "Please choose a ruleset!"
    } else if (node?.node_type === "scorecardset" && !node?.scorecard_set) {
      incompleteNodes = "Please choose a scorecardset!"
    } else if (node?.node_type === "program" && !node?.program) {
      incompleteNodes = "Please choose a program!"
    } else if (node?.node_type === "taglist" && !node?.taglist) {
      incompleteNodes = "Please choose a taglist!"
    } else if (node?.node_type === "project" && !node?.project) {
      incompleteNodes = "Please choose a project!"
    } else if (node?.node_type === "decision" && !node?.ruleset) {
      incompleteNodes = "Please choose a decision ruleset!"
    } else if (node?.node_type === "label" && !node?.return_label) {
      incompleteNodes = "Please add a return label!"
    } else if (node?.node_type === "filter" && node?.rulesValidations?.rules?.length > 0) {
      const filteredErrors = node?.rulesValidations?.rules?.filter((rule: FilterCondition) => !!rule)
      incompleteNodes = `${Object.values(filteredErrors[0])[0]} in filter`
    } else if (node?.equation === "(TODAY)" || ISOpattern.test(node?.equation)) {
      incompleteNodes = "Equation can't be a single date"
    } else if (node?.node_type === "label" && !node?.return_label.uuid) {
      incompleteNodes = `Label ${
        "(" + node?.return_label.name + ")"
      }: Choose an item from the dropdown to select a label`
    } else if (node?.node_type === "script" && node?.is_draft) {
      incompleteNodes = "Script node is draft, please save it first then add it to the workflow or remove it!"
    } else if (node?.node_type === "loop-start" && !node?.strategy) {
      incompleteNodes = "Please select a loop strategy, either Parallel or Combined!"
    } else if (node?.node_type === "loop-start" && !node?.name) {
      incompleteNodes = "Please add a loop name!"
    } else if (node?.node_type === "loop-start" && (node?.input_lists?.length === 0 || !node?.input_lists)) {
      incompleteNodes = "Please add at least one list in the loop!"
    }
  })

  if (areNodesInComplete) {
    NotificationUtils.toast("Workflow incomplete, please connect all workflow nodes!", { severity: "error" })
  } else if (incompleteNodes) {
    NotificationUtils.toast(incompleteNodes, { severity: "error" })
  } else if (duplicateNames && duplicateNames?.length > 0) {
    NotificationUtils.toast("Can't submit a duplicated workflow name, please change the workflow name!", {
      severity: "error",
    })
  } else {
    // getting the valid and filtered nodes
    const filteredNodes = correctFormat?.nodes?.map((node: WorkflowNode) => filterNodesKeys(node))

    // getting the start node
    const startNode = correctFormat?.nodes?.findIndex(
      (node: WorkflowNode & { parentId: string }) => node?.parentId === "1",
    )

    return { nodes: filteredNodes, startNode }
  }
}

export const handleCreateWorkflow = async (
  workflowName: string,
  nodes: Node[],
  workflowsData: WorkflowList[],
  createWorkflowMutation: UseMutationResult<AxiosResponse, AxiosError, CreateWorkflowRequest, unknown>,
  currentProjectId: string,
  setIsCreateMode: Dispatch<SetStateAction<boolean>>,
  edges: Edge[],
  schema: Array<SchemaFeature>,
  constants: Array<ConstantFeature>,
  setIsDuplicated?: Dispatch<SetStateAction<boolean>>,
): Promise<Node[] | undefined> => {
  try {
    const validWorkflow = validateWorkflow([...nodes], edges, workflowsData, workflowName, "create")

    if (validWorkflow && validWorkflow?.nodes?.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const clonedNodes = (JSON.parse(JSON.stringify(validWorkflow?.nodes)) as any[]) ?? []
      clonedNodes?.forEach((node) => {
        if (node.node_type === "label") node.return_label = node.return_label.uuid
      })

      if (validWorkflow?.nodes) {
        await createWorkflowMutation.mutateAsync({
          name: workflowName,
          state: workflowsData?.length > 0 ? WorkflowCreateRequest.state.DISABLED : WorkflowCreateRequest.state.ACTIVE,
          start_node: validWorkflow?.startNode && validWorkflow?.startNode !== -1 ? validWorkflow?.startNode : 0,
          nodes: clonedNodes?.length ? [...clonedNodes] : [...(validWorkflow?.nodes ?? [])],
          projectId: currentProjectId,
          schema: schema?.length > 0 ? schema : undefined,
          constants: constants?.length > 0 ? constants : undefined,
        })

        setIsCreateMode(false)
        setIsDuplicated?.(false)
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (e: any) {
    if (e?.response?.data?.details) {
      NotificationUtils.toast(`${e?.response?.data?.details}`, {
        severity: "error",
      })
    } else if (e?.response?.data?.nodes?.length > 0) {
      NotificationUtils.toast(`${e?.response?.data?.nodes[0]}`, {
        severity: "error",
      })
    } else if (e?.response?.data?.feature_mappings?.length > 0) {
      NotificationUtils.toast(`${e?.response?.data?.feature_mappings[0]}`, {
        severity: "error",
      })
    } else if (e?.response?.data?.non_field_errors?.length > 0) {
      NotificationUtils.toast(`${e?.response?.data?.non_field_errors[0]}`, {
        severity: "error",
      })
    } else {
      NotificationUtils.toast("An error occurred while creating this workflow, please try again!", {
        severity: "error",
      })
    }

    setIsCreateMode(true)
    return nodes
  }
}

// Function to compare two objects
function areSchemaObjectsDifferent(feature1: SchemaFeature, feature2: SchemaFeature): boolean {
  return (
    feature1.name !== feature2.name ||
    feature1.type !== feature2.type ||
    ("value" in feature1 && "value" in feature2 && feature1.value !== feature2.value) ||
    ("is_required" in feature1 && "is_required" in feature2 && feature1.is_required !== feature2.is_required)
  )
}

/**
 * Indicator for schema changes, used when we want to detect schema changes to decide whether
 * it should be included in the payload or not
 * @param currentSchema the current form for schema
 * @param retrievedSchema the original form (retrieved) for schema
 * @returns {boolean}
 */
export function hasSchemaChanged<T extends SchemaFeature | WorkflowConstantFeature>(
  currentFeatures: T[],
  retrievedFeatures: T[],
): boolean {
  // Check if arrays have same length
  if (currentFeatures?.length !== retrievedFeatures?.length || !currentFeatures || !retrievedFeatures) {
    return true
  }

  // Sort both arrays based on the 'name' property
  currentFeatures.sort((a, b) => a.name.localeCompare(b.name))
  retrievedFeatures.sort((a, b) => a.name.localeCompare(b.name))

  // Iterate through arrays and compare each object's properties
  for (let i = 0; i < currentFeatures.length; i++) {
    const obj1 = currentFeatures[i]
    const obj2 = retrievedFeatures[i]

    if (areSchemaObjectsDifferent(obj1 as SchemaFeature, obj2 as SchemaFeature)) {
      return true // If a difference is found, return true immediately
    }
  }

  return false // If no differences are found, return false
}

export const handleUpdateWorkflow = async (
  projectId: string,
  workflowUUID: string,
  updateWorkflowMutation: UseMutationResult<AxiosResponse, AxiosError, UpdateWorkflowRequest, unknown>,
  setIsEditMode: Dispatch<SetStateAction<boolean>>,
  workflowName: string,
  edges: Edge[],
  workflowsData: WorkflowList[],
  nodes: Node[],
  retrievedWorkflowName: string | undefined,
  schema: Array<SchemaFeature>,
  constants: Array<ConstantFeature>,
  setUpdatePayload: Dispatch<SetStateAction<UpdateWorkflowRequest | undefined>>,
  activeWorkflow?: WorkflowVersionRetrieve,
): Promise<Node[] | undefined> => {
  const validWorkflow = validateWorkflow(nodes, edges, workflowsData, workflowName, "update", retrievedWorkflowName)
  if (validWorkflow && validWorkflow?.nodes?.length > 0) {
    // TODO: change this any type later when backend have proper type for nodes
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const clonedNodes = (JSON.parse(JSON.stringify(validWorkflow?.nodes)) as any[]) ?? []

    clonedNodes.forEach((node) => {
      if (node.node_type === "label") node.return_label = node.return_label.uuid
    })

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const areAllNodesEqual = areNodesEqual(activeWorkflow?.nodes as any, nodes, edges)

    try {
      const features: Array<WorkflowInputFeature> = []
      schema?.forEach((elm) => {
        const objForm = {
          name: elm?.name?.trim() ?? "",
          type: elm?.type as WorkflowInputFeature.type,
          is_required: elm?.is_required,
          source: elm?.source as WorkflowInputFeature.source,
        }

        features?.push(objForm)
      })

      if (areAllNodesEqual && validWorkflow?.nodes?.length > 0) {
        const baseMutationParams = {
          name: workflowName,
          projectId,
          workflowUUID,
          constants,
        }

        const mutationParams = hasSchemaChanged(
          features as SchemaFeature[],
          activeWorkflow?.schema?.features as SchemaFeature[],
        )
          ? { ...baseMutationParams, schema: [...features] }
          : baseMutationParams

        setUpdatePayload(mutationParams)
        await updateWorkflowMutation.mutateAsync(mutationParams)
      } else if (nodes && validWorkflow?.nodes?.length) {
        const baseMutationParams = {
          name: workflowName,
          start_node: validWorkflow?.startNode && validWorkflow?.startNode !== -1 ? validWorkflow?.startNode : 0,
          nodes: clonedNodes,
          projectId,
          workflowUUID,
          constants,
        }

        const mutationParams = hasSchemaChanged(
          features as SchemaFeature[],
          activeWorkflow?.schema?.features as SchemaFeature[],
        )
          ? { ...baseMutationParams, schema: [...features] }
          : baseMutationParams

        setUpdatePayload(mutationParams)
        await updateWorkflowMutation.mutateAsync(mutationParams)
      }
    } catch (e) {
      console.warn(e)
      setIsEditMode(true)
      return nodes
    }
  }
}

export const getLayoutedElements = (nodes: Node[], edges: Edge[], direction = "TB"): void => {
  const dagreGraph = new dagre.graphlib.Graph()
    .setGraph({ rankdir: direction, ranksep: 100, nodesep: 150 })
    .setDefaultEdgeLabel(() => ({}))

  const calculateNodeDimensions = (node: Node, edges: Edge[]): { width: number; height: number } => {
    let width = 350
    let height = 100

    switch (node?.type) {
      case "FilterNode":
        height = node?.data?.readMode ? 100 + node?.data?.rules?.length * 90 : 100 + node?.data?.rules?.length * 130
        width = 750
        break
      case "CalculatorNode":
        height = node?.data?.isUnfolded ? 100 + 30 + node?.data?.calculator?.equations?.length * 52 : 100
        break
      case "AddBlockNode":
        width = 125
        height = 60
        break
      case "LoopStartNode":
        width = 400
        height = node?.data?.readMode && node?.data?.input_lists?.length === 0 ? height : 150
        break
      case "LoopEndNode":
        const targetHandles = edges.filter((edge) => edge?.target === node?.id)
        width = targetHandles?.length > 1 ? targetHandles?.length * 400 : 400
        height = 80
        break
      default:
        if (["RulesetNode", "DecisionNode", "EndResultNode"].includes(node?.data?.nodeType)) {
          height = isNodeInLoop(node?.id, nodes)?.isNodeInLoop && !node?.data?.readMode ? 170 : 100
        }
    }

    return { width, height }
  }

  nodes.forEach((node: Node) => {
    const { width, height } = calculateNodeDimensions(node, edges)
    dagreGraph.setNode(node.id, { width, height })
  })

  edges.forEach((edge: Edge) => {
    dagreGraph.setEdge(edge.source, edge.target)
  })

  dagre.layout(dagreGraph)

  nodes.forEach((node: Node) => {
    const nodeWithPosition = dagreGraph.node(node.id)
    const { width, height } = calculateNodeDimensions(node, edges)

    if (node?.targetPosition && node.sourcePosition) {
      node.targetPosition = Position?.Top
      node.sourcePosition = Position.Bottom
    }

    node.position = {
      x: nodeWithPosition.x - width / 2,
      y: nodeWithPosition.y - height / 2,
    }

    node.data = {
      ...node.data,
      height, // Store dynamic height
    }
  })
}

export const findBiggerVersion = (num: number, arr: (number | string)[]): number | string => {
  let maxVersion: number | string = num
  let foundFloatVersion = false

  for (const element of arr) {
    const arrNum = typeof element === "number" ? element : parseFloat(element)

    if (arrNum === num) {
      continue // skip checking the original number
    }

    if (arrNum > maxVersion && arrNum < num + 1) {
      // check if arrNum is between the original number and the next integer
      foundFloatVersion = true
      maxVersion = arrNum
    } else if (arrNum === Math.floor(num)) {
      maxVersion = num // the array contains the original number as an integer, return it
      break
    }
  }

  return foundFloatVersion ? maxVersion : num
}

export function getWorkflowByUUID(
  uuid: string | undefined,
  workflows: Array<WorkflowList> | undefined,
): WorkflowList | null {
  if (!workflows || !uuid) return null
  return workflows?.find((workflow) => workflow.uuid === uuid) || null
}

export const getEdgeLabel = (nodeType: string, index: number): string => {
  const edgeLabels = {
    ruleset: "Uncovered",
    filter: ["true", "false"],
    none: "",
  }
  if (["RulesetNode", "DecisionNode"].includes(nodeType)) {
    return edgeLabels["ruleset"]
  } else if (nodeType === "FilterNode") {
    return edgeLabels["filter"][index] as string
  } else {
    return edgeLabels["none"]
  }
}

export const isStaticAnalysisValid = (text: string): { valid: boolean; reason?: string } => {
  if (!text?.includes("main()")) {
    return { valid: false, reason: "Can't remove main function from script" }
  }
  return { valid: true }
}

/**
 * Loops through list to find item with given key that matches query key
 * then returned trimmed name w/underscores instead of spaces of this item name
 * @param list list of items we iterating on, can be list of strings or objects
 * @param word
 * @param key
 * @returns {string}
 */
export function getTrimmedComputedFeatureName<T>(list: Array<T> | undefined, word: string, key: keyof T): string {
  const item = list?.find((item) => item[key] === word)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const nonTrimmedName = (item as any)?.name
  return nonTrimmedName?.replace(/\s+/g, "_")
}

/**
 * Transforms a list of schema features to a structured format. Each schema feature is
 * mapped to a new object that includes the feature's name, type, and the names of the
 * workflows sourced from the feature. The transformation capitalizes on utilizing
 * the feature's inherent properties to organize and prepare them for further processing
 * or display.
 *
 * @param {Array<WorkflowDeploymentSchemaFeature>} schemaFeatures - An array of `WorkflowDeploymentSchemaFeature` objects representing
 * the schema features to be mapped.
 * @param {FeatureData["featureCategory"]} featureCategory - The category to assign to each feature ("input" | "computed" | "constant").
 * @returns {Array<FeatureData>} An array of `FeatureData` objects. Each object includes the `name` of the
 * feature, its `type`, and an array of `workflows` names derived from the feature's source workflows.
 */
export const mappedSchemaFeatures = (
  schemaFeatures:
    | Array<WorkflowDeploymentSchemaFeature>
    | Array<WorkflowDeploymentConstantFeature>
    | Array<ModelDeploymentSchemaFeature>,
  featureCategory: "input" | "constant",
): Array<FeatureData> => {
  if (schemaFeatures.length === 0) {
    return []
  }

  // Check the type of the first element to determine the array type
  const firstFeature = schemaFeatures[0]
  const hasSourceWorkflows = firstFeature && "source_workflows" in firstFeature

  if (hasSourceWorkflows) {
    return schemaFeatures?.map((feature) => ({
      name: feature.name,
      type: feature.type,
      isRequired: featureCategory === "input" ? (feature as WorkflowDeploymentSchemaFeature)?.is_required : false,
      workflows: (
        feature as WorkflowDeploymentSchemaFeature | WorkflowDeploymentConstantFeature
      )?.source_workflows?.map((feat) => ({
        name: feat?.name,
        version: feat?.version,
      })),
      featureCategory,
    }))
  }

  return schemaFeatures?.map((feature) => ({
    name: feature.name,
    type: feature.type,
    isRequired: (feature as ModelDeploymentSchemaFeature)?.is_required,
    workflows: (feature as ModelDeploymentSchemaFeature)?.source_models?.map((feat) => ({
      name: feat?.name,
      version: "",
    })),
    featureCategory,
  }))
}

/**
 * Processes an array of computed features to group them by their names and associate
 * each group with its corresponding workflows. If a feature name already exists in the
 * accumulator, the workflow name is added to the existing group. Otherwise, a new group
 * is created for that feature name. This function is particularly useful for aggregating
 * computed feature names and their respective workflows, aiding in data organization and
 * retrieval. It also ensures each feature name is formatted with the first letter capitalized
 * and the remaining letters in lowercase for consistency.
 *
 * @param {Array<ComputedFeatures> | null} computedFeatures - An optional array of `ComputedFeatures` objects representing
 * the computed features to be processed. If null or empty, the function returns `void`.
 * @returns {Array<FeatureData>} An array of `FeatureData` objects, or `void` if `computedFeatures` is null or empty.
 * Each `FeatureData` object contains the capitalized `name` of the feature, an array of `workflows`
 * associated with that feature, and a `type` defaulted to `WorkflowInputFeature.type.UNDEFINED`.
 */
export const mappedComputedFeaturesNames = (computedFeatures: Array<ComputedFeatures> | null): Array<FeatureData> => {
  if (computedFeatures && computedFeatures?.length > 0) {
    return computedFeatures.reduce(
      (formattedComputedFeatures: FeatureData[], currentComputedFeature: ComputedFeatures) => {
        currentComputedFeature.computed_feature_names.forEach((featureName: string) => {
          const featureIndex = formattedComputedFeatures.findIndex(
            (feature: FeatureData) => feature.name.toLowerCase() === featureName.toLowerCase(),
          )
          if (featureIndex !== -1) {
            formattedComputedFeatures[featureIndex]?.workflows.push({
              name: currentComputedFeature.workflow_name,
              version: "",
            })
          } else {
            formattedComputedFeatures.push({
              name: featureName,
              workflows: [
                {
                  name: currentComputedFeature.workflow_name,
                  version: "",
                },
              ],
              type: WorkflowInputFeature.type.UNDEFINED,
              featureCategory: "computed",
            })
          }
        })
        return formattedComputedFeatures
      },
      [],
    )
  }
  return []
}

/**
 * Handles the deletion of selected nodes and their associated edges in a graph.
 * This function updates the edges to remove any connections to the deleted nodes,
 * and then updates the nodes to reflect these deletions. It automatically manages
 * the reconnection of nodes that were indirectly connected through the deleted nodes
 * by creating new edges between them. This function is designed to be reused in any
 * part of the application where node and edge deletion with reconnection logic is required.
 *
 * @param {Node[]} deleted - An array of Node objects that are to be deleted.
 * @param {Edge[]} edges - The current array of Edge objects in the graph.
 * @param {Node[]} getNodes - A function that returns the current array of Node objects.
 * @param {string} nodeId - The ID of the node being directly interacted with or deleted.
 * @return {object} {nodes:Node[], edges:Edge[]}
 */
export function handleDeletingNodeInBetween(
  deleted: Node[],
  edges: Edge[],
  nodes: Node[],
  nodeId: string,
): { nodes: Node[]; edges: Edge[] } {
  // Process edges and capture the resulting edges array
  const newEdges = deleted.reduce((acc, node) => {
    /**
     * We create another array of edges by flatMapping over the array of incomers.
     * These are nodes that were connected to the deleted node as a source.
     * For each of these nodes, we create a new edge that connects to each node in the array of outgoers.
     * These are nodes that were connected to the deleted node as a target.
     */
    const incomers = getIncomers(node, nodes, edges)
    const outgoers = getOutgoers(node, nodes, edges)

    // getConnectedEdges gives us all the edges connected to a node, either as source or target.
    const connectedEdges = getConnectedEdges([node], edges)

    // We create a new array of edges - remainingEdges -
    // that contains all the edges in the flow that have nothing to do with the node(s) we just deleted.
    const remainingEdges = acc.filter((edge) => !connectedEdges.includes(edge))

    // Instead of creating entirely new edges, we find the original edge from the incomer to the deleted node
    // and modify its target to point to each outgoer. Preserve all original edge data.
    const createdEdges = incomers.flatMap((incomer) =>
      outgoers.map((outgoer) => {
        // Find the original edge that connected the incomer to the deleted node.
        const originalEdge = edges.find((edge) => edge.source === incomer.id && edge.target === node.id)

        const originalTargetEdge = edges.find((edge) => edge.target === outgoer.id && edge.source === node.id)

        // If there's no original edge (which should theoretically never happen in this setup), create a new edge.
        // Otherwise, create a modified copy of the original edge with a new target.
        return originalEdge
          ? {
              ...originalEdge,
              source: incomer.id,
              target: outgoer.id,
              id: `${incomer.id}->${outgoer.id}-${uuidv4()}`,
              targetHandle: originalTargetEdge?.targetHandle,
            }
          : {
              id: `${incomer.id}->${outgoer.id}-${uuidv4()}`,
              source: incomer.id,
              target: outgoer.id,
              targetHandle: `${uuidv4()}`,
            }
      }),
    )
    // this is to make sure always the true branch (sourceHandle = a), always comes first before
    // false branch (sourceHandle = b), otherwise edges in filter will visually swap
    if (createdEdges[0]?.data?.text === "true" && createdEdges[0]?.sourceHandle === "a") {
      return [...createdEdges, ...remainingEdges]
    } else {
      return [...remainingEdges, ...createdEdges]
    }
  }, edges)

  // Process nodes and capture the resulting nodes array
  const newNodes = nodes
    .map((node) => {
      const newNode = { ...node }

      if (node?.data?.parentId === nodeId) {
        newNode.data = {
          ...newNode.data,
          parentId: deleted[0]?.data?.parentId,
        }
      }
      return newNode
    })
    .filter((node) => node.id !== nodeId)

  // Return an object with the new nodes and edges arrays
  return {
    nodes: newNodes,
    edges: newEdges,
  }
}

export function filteredFeaturesBasedOnType(
  feature: string,
  features: Array<SchemaFeature>,
  inputFeatures: ScriptSchemaFeature[],
): Array<SchemaFeature> {
  const existingFeatureType = inputFeatures?.find((feat) => feat?.name === feature)?.type

  if (existingFeatureType === WorkflowInputFeature.type.UNDEFINED) {
    return features
  } else {
    return features?.filter(
      (feat) => feat?.type === existingFeatureType || feat?.type === WorkflowInputFeature.type.UNDEFINED,
    )
  }
}

/**
 * Checks if a node is inside a loop.
 *
 * This function determines if a node with the specified `sourceNodeId` is part of a loop by
 * searching for the node in the provided `nodes` array and checking its type, node type, or data properties.
 *
 * @param {string} sourceNodeId - The ID of the source node to check.
 * @param {ExtendedNodeData[]} nodes - An array of nodes to search through, extended to include `uuid` and `node_type`.
 * @returns {boolean} `true` if the node is part of a loop; otherwise, `false`.
 */
export const isNodeInLoop = (
  sourceNodeId: string,
  nodes: ExtendedNodeData[],
): { isNodeInLoop: boolean; isNodeInNestedLoop: boolean; loopId: string } => {
  const parentNode = nodes.find((node) => node.id === sourceNodeId)

  return {
    isNodeInLoop:
      sourceNodeId === "1"
        ? false
        : parentNode?.node_type === "loop-start" ||
          parentNode?.data?.nodeType === "LoopStartNode" ||
          (parentNode?.data?.isNodeInLoop && parentNode?.data?.nodeType !== "LoopEndNode") ||
          (parentNode?.data?.nodeType === "LoopEndNode" && parentNode?.data?.isNodeInNestedLoop),

    isNodeInNestedLoop:
      sourceNodeId === "1"
        ? false
        : parentNode?.data?.isNodeInNestedLoop && parentNode?.data?.nodeType !== "LoopEndNode",

    loopId: parentNode?.data?.loopId ?? "",
  }
}

/**
 * Determines if a node is in a filter and returns related filter information.
 *
 * @param sourceNodeId - The ID of the source node to check.
 * @param nodes - An array of nodes to search within.
 * @returns An object containing:
 *  - isNodeInFilter: A boolean indicating if the node is in a filter.
 *  - filterBranch: The filter branch status ("true", "false", or null).
 *  - filterId: The ID of the filter.
 */
export const isNodeInFilter = (
  sourceNodeId: string,
  nodes: ExtendedNodeData[],
  nodeId?: string,
): { isNodeInFilter: boolean; filterBranch: "true" | "false" | null; filterId: string } => {
  const parentNode = nodes.find((node) => node.id === sourceNodeId)

  let filterBranchOnRetrieve = ""

  if (nodeId && parentNode?.data?.nodeType === "FilterNode") {
    filterBranchOnRetrieve =
      parentNode?.data?.true_route === nodeId ? "true" : parentNode?.data?.false_route === nodeId ? "false" : ""
  }

  return {
    isNodeInFilter:
      parentNode?.data?.nodeType === "FilterNode" ||
      parentNode?.node_type === "filter" ||
      parentNode?.data?.isNodeInFilter,

    filterBranch: parentNode?.data?.filterBranch || filterBranchOnRetrieve,

    filterId: parentNode?.data?.filterId || parentNode?.data?.id || "",
  }
}

/**
 * Calculates the X position of a node handle based on its index, node width, and the total number of handles.
 *
 * @param index - The index of the handle (starting from 0).
 * @param nodeWidth - The width of the node.
 * @param numHandles - The total number of handles on the node.
 * @returns The X position of the handle.
 */
export const getNodeHandleXPosition = (index: number, nodeWidth: number, numHandles: number): number => {
  const handleWidth = nodeWidth / numHandles
  const handleCenterX = handleWidth / 2
  if (numHandles === 1) {
    return handleCenterX
  }

  return index * handleWidth + handleCenterX
}

/**
 * Retrieves all descendant nodes of a given node until a loop end node is reached, based on the deletion case.
 *
 * @param nodes - The array of all nodes.
 * @param nodeId - The ID of the starting node.
 * @param deletionCase - The case determining the deletion criteria, which can be "loop-start", "filter-outside-loop", or "filter-inside-loop".
 * @returns An array of descendant nodes.
 */
export const getDescendantNodes = (
  nodes: Node[],
  nodeId: string,
  deletionCase: "loop-start" | "filter-outside-loop" | "filter-inside-loop",
  maxDepth = 100, // optional parameter to limit depth
): Node[] => {
  const descendants: Node[] = []
  const visited = new Set<string>()

  const originalNode = nodes?.find((node) => node.id === nodeId)
  if (!originalNode) {
    // If the original node is not found, return an empty array
    return descendants
  }

  const stack: { node: Node; depth: number }[] = [{ node: originalNode, depth: 0 }]

  while (stack.length > 0) {
    const { node: currentNode, depth = 0 } = stack.pop() ?? {}
    if (!currentNode || visited.has(currentNode.id) || depth > maxDepth) continue

    visited.add(currentNode.id)

    const children = nodes.filter((node) => node.data?.parentId === currentNode.id)

    for (const child of children) {
      const shouldAddToDescendants = !(
        deletionCase === "filter-inside-loop" &&
        child.data?.nodeType === "LoopEndNode" &&
        child.data?.parentLoopId === originalNode.data?.loopId
      )

      const shouldAddNodeToStack = !(
        (deletionCase === "loop-start" || deletionCase === "filter-inside-loop") &&
        child.data?.nodeType === "LoopEndNode" &&
        child.data?.parentLoopId === originalNode.data?.loopId
      )

      if (shouldAddToDescendants) {
        descendants.push(child)
      }

      if (shouldAddNodeToStack) {
        stack.push({ node: child, depth: depth + 1 })
      }
    }
  }

  return descendants
}

/**
 * Custom hook to retrieve workflow features based on specified options.
 * Returns all features if no options are provided.
 *
 * @param {Set<"SchemaFeatures" | "ComputedFeatures" | "ConstantFeatures">} options - A set of allowed options to filter the features.
 * @returns {(FeatureData)[]} An array of features based on the specified options.
 */
export const useWorkflowFeatures = (
  options?: Set<"SchemaFeatures" | "ComputedFeatures" | "ConstantFeatures">,
): FeatureData[] => {
  const schemaFeatures = useSelector((state: RootState) => state?.project?.schemaFeatures)
  const computedSchemaFeatures = useSelector((state: RootState) => state?.project?.computedFeatures)
  const constantFeatures = useSelector((state: RootState) => state?.project?.constantFeatures)

  const features: FeatureData[] = []

  if (!options) return [...schemaFeatures, ...computedSchemaFeatures, ...constantFeatures]

  options.forEach((option) => {
    switch (option) {
      case "SchemaFeatures":
        features.push(...schemaFeatures)
        break
      case "ComputedFeatures":
        features.push(...computedSchemaFeatures)
        break
      case "ConstantFeatures":
        features.push(...constantFeatures)
        break
      default:
        break
    }
  })

  return features
}

/**
 * Validates that each feature's value matches its declared type.
 *
 * This function is used to ensure that all values in the given `WorkflowConstantFeature[]`
 * are compatible with their corresponding declared `type` property.
 *
 * Supported types include:
 * - TEXT: Must be a string
 * - NUMBER: Can be a number or numeric string (excluding empty/whitespace-only)
 * - BOOLEAN: Must be a boolean or the strings "true"/"false"
 * - DATE: Must be a valid date string (ISO 8601 recommended)
 * - LIST: Can be an array or a JSON string representing an array
 * - DICT: Must be a plain object (non-array, non-null)
 *
 * @param features - The list of workflow constant features to validate
 * @returns The name of the first feature whose value does not match the declared type, or `null` if all values are valid
 */
export function checkIfValuesMatchTypes(features: WorkflowConstantFeature[]): string | null {
  for (const feature of features) {
    const { name, type, value } = feature

    let isValid = false

    switch (type) {
      case WorkflowConstantFeature.type.TEXT:
        isValid = typeof value === "string"
        break
      case WorkflowConstantFeature.type.NUMBER:
        isValid =
          (typeof value === "number" || (typeof value === "string" && value.trim() !== "" && !isNaN(Number(value)))) &&
          value !== ""
        break
      case WorkflowConstantFeature.type.BOOLEAN:
        isValid = typeof value === "boolean" || (typeof value === "string" && (value === "true" || value === "false"))
        break
      case WorkflowConstantFeature.type.DATE:
        const date = typeof value === "string" ? new Date(value) : value
        isValid = date instanceof Date && !isNaN(date.getTime())
        break
      case WorkflowConstantFeature.type.LIST:
        isValid =
          Array.isArray(value) ||
          (typeof value === "string" &&
            (() => {
              try {
                const parsed = JSON.parse(value)
                return Array.isArray(parsed)
              } catch {
                return false
              }
            })())
        break
      default:
        return name // Unknown type
    }

    if (!isValid) {
      return name
    }
  }

  return null // All features are valid
}
